// Theme management — light / dark.
// Precedence: explicit user choice (localStorage 'linker_theme') > OS preference
// (prefers-color-scheme) > light. When the user hasn't made an explicit choice,
// we follow the OS live. getTheme() is also mirrored inline in public/index.html
// so the correct theme applies before Vue mounts (avoids a flash).

const STORAGE_KEY = 'linker_theme';

// The explicit, user-set preference, or null if they've never toggled.
export function storedTheme() {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    if (t === 'light' || t === 'dark') return t;
  } catch (e) { /* ignore */ }
  return null;
}

export function osTheme() {
  return (typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-color-scheme: dark)').matches)
    ? 'dark' : 'light';
}

// Effective theme to render right now.
export function getTheme() {
  return storedTheme() || osTheme();
}

export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('theme-dark');
  else root.classList.remove('theme-dark');
}

// Persist an explicit user choice.
export function setTheme(theme) {
  applyTheme(theme);
  try { localStorage.setItem(STORAGE_KEY, theme); } catch (e) { /* ignore */ }
  return theme;
}

export function toggleTheme() {
  const next = getTheme() === 'dark' ? 'light' : 'dark';
  return setTheme(next);
}

// Apply the effective theme once at startup and keep following the OS while the
// user has no explicit preference. Returns an unsubscribe fn (rarely needed).
export function initTheme() {
  applyTheme(getTheme());
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => {
      if (!storedTheme()) applyTheme(e.matches ? 'dark' : 'light');
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }
  return () => {};
}
